import DateFnsUtils from '@date-io/date-fns';
import { Box, IconButton, InputAdornment } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import { endOfDay, isAfter, isBefore, isEqual } from 'date-fns';
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isValid from "date-fns/isValid";
import isWithinInterval from "date-fns/isWithinInterval";
import startOfDay from "date-fns/startOfDay";
import PropTypes from 'prop-types';
import { default as React, useState } from 'react';

const datePickerStyles = makeStyles(theme => ({
    dayWrapper: {
        position: "relative",
    },
    day: {
        width: 36,
        height: 36,
        fontSize: theme.typography.caption.fontSize,
        margin: "2px",
        color: "inherit",
    },
    customDayHighlight: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: "2px",
        right: "2px",
        border: `1px solid ${theme.palette.secondary.main}`,
        borderRadius: "50%",
    },
    nonCurrentMonthDay: {
        color: theme.palette.text.disabled,
    },
    dayDisabled: {
        color: "rgba(0, 0, 0, 0.38)",
        pointerEvents: "none"
    },
    highlightNonCurrentMonthDay: {
        color: "#676767",
    },
    highlight: {
        background: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    firstHighlight: {
        extend: "highlight",
        borderTopLeftRadius: "50%",
        borderBottomLeftRadius: "50%",
    },
    endHighlight: {
        extend: "highlight",
        borderTopRightRadius: "50%",
        borderBottomRightRadius: "50%",
    },
}));

/**
 * Custom date picker allowing for date ranges to be selected
 * @param {*} props Props for customising the Data Picker
 */
function CustomDatePicker(props) {

    const datePickerClasses = datePickerStyles()

    const [dateToggle, setDateToggle] = useState(false)

    /**
     * Handle the date selection, based on which date is being updated
     * @param {DateIOType} date Date selected
     */
    function handleDateSelect(date) {
        const { firstDateSelected } = props

        const newDate = startOfDay(date);
        if (dateToggle) {
            props.updateFirstDate(newDate)
            props.updateSecondDate(null)
            setDateToggle(!dateToggle)
        } else if (isAfter(newDate, firstDateSelected) || isEqual(newDate, firstDateSelected)) {
            props.updateSecondDate(endOfDay(newDate))
            setDateToggle(!dateToggle)
        } else if (isBefore(newDate, firstDateSelected)) {
            props.updateSecondDate(endOfDay(firstDateSelected))
            props.updateFirstDate(newDate)
            setDateToggle(true)
        }
    };

    /**
     * String formatter for the dates selected
     * @param {DateIOType} date Date selected
     * @param {string} invalidLabel String label of invalid dates
     */
    function formatDateSelectLabel(date, invalidLabel) {
        const { firstDateSelected, secondDateSelected } = props
        return firstDateSelected && isValid(firstDateSelected)
            ? `${firstDateSelected ? format(firstDateSelected, "dd/MM/yy") : ""} - ${secondDateSelected ? format(secondDateSelected, "dd/MM/yy") : ""}`
            : "";
    };

    /**
     * Renderer for the selected dates
     * @param {DateIOType} date Current day
     * @param {DateIOType} selectedDate The selected date
     * @param {boolean} dayInCurrentMonth Does the selected date fall in the current month
     * @param {JSX.Element} dayComponent Component of the date
     */
    function renderDatesSelection(date, selectedDate, dayInCurrentMonth, dayComponent) {
        let dateClone = date;

        const start = props.firstDateSelected;
        const end = props.secondDateSelected;


        const dayIsBetween = start && end ? isWithinInterval(dateClone, { start, end }) : start ? isSameDay(dateClone, start) : false

        const isFirstDay = start ? isSameDay(dateClone, start) : false
        const isLastDay = end ? isSameDay(dateClone, end) : false

        const wrapperClassName = clsx({
            [datePickerClasses.highlight]: dayIsBetween,
            [datePickerClasses.firstHighlight]: isFirstDay,
            [datePickerClasses.endHighlight]: isLastDay,
        });

        const dayClassName = clsx(datePickerClasses.day, {
            [datePickerClasses.highlightNonCurrentMonthDay]: !dayInCurrentMonth && dayIsBetween,
            [datePickerClasses.dayDisabled]: isAfter(date, new Date()),
            [datePickerClasses.nonCurrentMonthDay]: !dayInCurrentMonth,
        });

        return (
            <div className={wrapperClassName}>
                <IconButton className={dayClassName}>
                    <span> {format(dateClone, "d")} </span>
                </IconButton>
            </div>
        );
    };

    /**
     * Handler for preventing default mouse event
     * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event 
     */
    function handleDownPress(event) {
        event.preventDefault();
    };

    return (
        <Box>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DatePicker
                    fullWidth
                    margin="dense"
                    disableToolbar
                    variant="inline"
                    inputVariant="outlined"
                    label={props.label}
                    disableFuture
                    value={props.firstDateSelected}
                    onChange={(date) => {
                        handleDateSelect(date)
                    }}
                    renderDay={renderDatesSelection}
                    labelFunc={formatDateSelectLabel}
                    InputLabelProps={{ shrink: props.firstDateSelected }}
                    onMouseDown={handleDownPress}
                    InputProps={{
                        endAdornment:
                            <InputAdornment position="end" >
                                {
                                    props.firstDateSelected === null ? null : <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            props.updateFirstDate(null);
                                            props.updateSecondDate(null);
                                            setDateToggle(true);
                                        }}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                }
                            </InputAdornment>
                    }}
                />
            </MuiPickersUtilsProvider>
        </Box>
    )
}

CustomDatePicker.propTypes = {
    label: PropTypes.string,
    firstDateSelected: PropTypes.instanceOf(Date).isRequired,
    secondDateSelected: PropTypes.instanceOf(Date),
    updateFirstDate: PropTypes.func.isRequired,
    updateSecondDate: PropTypes.func.isRequired
}

export default CustomDatePicker

